export function INeedToSellMyApartments() {
  return (
    <section className="py-20 bg-white" id="need-to-sell">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            I Need to Sell My Apartments - What Are My Options?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you're facing financial difficulties, relocating, or simply want to liquidate your investment, 
            understanding your options helps you make the right decision for your situation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Common Reasons for Selling Apartments</h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-money-dollar-circle-line text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Financial Distress</h4>
                  <p className="text-gray-600">
                    Facing foreclosure, high vacancy rates, or can't afford maintenance and repairs. 
                    Cash flow problems make it difficult to maintain the property profitably.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-home-line text-blue-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Property Management Burnout</h4>
                  <p className="text-gray-600">
                    Tired of dealing with tenant issues, maintenance calls, and day-to-day property management responsibilities. 
                    Ready to move on to passive investments.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-exchange-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Portfolio Rebalancing</h4>
                  <p className="text-gray-600">
                    Want to diversify investments, upgrade to larger properties, or move capital to different markets 
                    with better growth potential.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-time-line text-purple-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Life Changes</h4>
                  <p className="text-gray-600">
                    Retirement, relocation, health issues, or inheritance situations requiring quick liquidation 
                    of real estate assets.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/61a1d402b8c7a99abbc7e45007dbc1a7.jpg"
              alt="Property owner dealing with apartment building challenges"
              className="w-full h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Your Selling Options Compared
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Selling Method</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Timeline</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Fees/Costs</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Repairs Needed</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Best For</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="bg-orange-50">
                  <td className="px-6 py-4 font-semibold text-orange-800">Direct Cash Sale</td>
                  <td className="px-6 py-4 text-center text-green-600 font-medium">7-30 days</td>
                  <td className="px-6 py-4 text-center text-green-600 font-medium">0-2%</td>
                  <td className="px-6 py-4 text-center text-green-600 font-medium">None</td>
                  <td className="px-6 py-4 text-center text-sm">Quick sale needed</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Real Estate Agent</td>
                  <td className="px-6 py-4 text-center text-red-600">6-12 months</td>
                  <td className="px-6 py-4 text-center text-red-600">6-10%</td>
                  <td className="px-6 py-4 text-center text-red-600">Usually</td>
                  <td className="px-6 py-4 text-center text-sm">Maximum price</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Auction</td>
                  <td className="px-6 py-4 text-center text-yellow-600">30-60 days</td>
                  <td className="px-6 py-4 text-center text-yellow-600">8-15%</td>
                  <td className="px-6 py-4 text-center text-yellow-600">Sometimes</td>
                  <td className="px-6 py-4 text-center text-sm">Competitive bidding</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Owner Financing</td>
                  <td className="px-6 py-4 text-center text-yellow-600">30-90 days</td>
                  <td className="px-6 py-4 text-center text-green-600">2-4%</td>
                  <td className="px-6 py-4 text-center text-yellow-600">Maybe</td>
                  <td className="px-6 py-4 text-center text-sm">Higher sale price</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white border-2 border-orange-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-flashlight-line text-3xl text-orange-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Emergency Sale Needed</h4>
            <p className="text-gray-600 mb-6">
              Facing foreclosure, divorce, or other urgent situations requiring immediate property liquidation.
            </p>
            <div className="bg-orange-50 p-4 rounded-lg">
              <p className="text-sm text-orange-800 font-medium">Recommended: Direct Cash Sale</p>
              <p className="text-sm text-gray-600 mt-1">Close in 7-14 days</p>
            </div>
          </div>

          <div className="bg-white border-2 border-blue-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-scales-3-line text-3xl text-blue-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Want Maximum Value</h4>
            <p className="text-gray-600 mb-6">
              Have time to wait and want to achieve the highest possible sale price for your apartment building.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">Recommended: Traditional Listing</p>
              <p className="text-sm text-gray-600 mt-1">6-12 month process</p>
            </div>
          </div>

          <div className="bg-white border-2 border-green-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-balance-line text-3xl text-green-600"></i>
            </div>
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Balance Speed & Price</h4>
            <p className="text-gray-600 mb-6">
              Want a reasonable price but don't want to wait a full year or deal with extensive marketing.
            </p>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800 font-medium">Recommended: Cash Buyer</p>
              <p className="text-sm text-gray-600 mt-1">Fair price, 30-day close</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Get Your Situation Evaluated - Free Consultation
          </h3>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Every apartment building sale is unique. Let us analyze your specific situation and provide personalized 
            recommendations for the best selling strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-orange-600 px-10 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 whitespace-nowrap">
              Schedule Free Consultation
            </button>
            <button className="border-2 border-white text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-orange-600 whitespace-nowrap">
              Call Now: (555) 123-4567
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}